'use client';

import { c as _c } from "react/compiler-runtime";
import { SubheadingH3 } from '@/components/ui/typography/subheading-h3';
import { changeUserText, loggedInAsText, loginWithCeviDatabaseText } from '@/features/payload-cms/components/form/static-form-texts';
import { getFormStorageKey } from '@/features/payload-cms/components/form/utils/get-form-storage-key';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import { useSignOut } from '@/hooks/use-sign-out';
import type { StaticTranslationString } from '@/types/types';
import { i18nConfig, type Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { signIn, useSession } from 'next-auth/react';
import React, { useEffect } from 'react';
import type { FieldError, FieldErrorsImpl, FieldValues, Merge } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
interface CeviDatabaseLoginProperties {
  name: string;
  label?: string;
  saveField?: 'name' | 'uuid' | 'email' | 'nickname';
  fieldMapping?: {
    jwtField: string;
    formField: string;
  }[];
  formId?: string;
  required?: boolean;
  currentStepIndex?: number;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>>;
}
const loginRequiredMessage: StaticTranslationString = {
  en: 'You must log in to proceed',
  de: 'Sie müssen sich anmelden, um fortzufahren',
  fr: 'Vous devez vous connecter pour continuer'
};
export const CeviDatabaseLogin: React.FC<CeviDatabaseLoginProperties> = t0 => {
  const $ = _c(48);
  const {
    name,
    label,
    required,
    saveField,
    fieldMapping,
    formId,
    currentStepIndex,
    error
  } = t0;
  const {
    register,
    setValue,
    getValues
  } = useFormContext();
  const {
    data: session
  } = useSession();
  const currentLocale = useCurrentLocale(i18nConfig);
  const locale = (currentLocale ?? "en") as Locale;
  const signOutAndForget = useSignOut();
  let t1;
  if ($[0] !== currentStepIndex || $[1] !== formId || $[2] !== getValues) {
    t1 = () => {
      const values = getValues();
      if (typeof formId === "string" && formId !== "") {
        sessionStorage.setItem(getFormStorageKey(formId, "state"), JSON.stringify(values));
        if (currentStepIndex !== undefined) {
          sessionStorage.setItem(getFormStorageKey(formId, "step"), String(currentStepIndex));
        }
      }
      if (typeof globalThis !== "undefined" && globalThis.history) {
        globalThis.history.replaceState(undefined, "", globalThis.location.href);
      }
      const callbackUrl = typeof globalThis === "undefined" ? undefined : globalThis.location.href;
      const signInOptions = {};
      if (typeof callbackUrl === "string" && callbackUrl !== "") {
        signInOptions.callbackUrl = callbackUrl;
      }
      signIn("cevi-db", signInOptions);
    };
    $[0] = currentStepIndex;
    $[1] = formId;
    $[2] = getValues;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleLogin = t1;
  let t2;
  if ($[4] !== currentStepIndex || $[5] !== formId || $[6] !== getValues || $[7] !== signOutAndForget) {
    t2 = () => {
      const values_0 = getValues();
      if (typeof formId === "string" && formId !== "") {
        sessionStorage.setItem(getFormStorageKey(formId, "state"), JSON.stringify(values_0));
        if (currentStepIndex !== undefined) {
          sessionStorage.setItem(getFormStorageKey(formId, "step"), String(currentStepIndex));
        }
      }
      const callbackUrl_0 = typeof globalThis === "undefined" ? undefined : globalThis.location.href;
      const signInOptions_0 = {};
      if (typeof callbackUrl_0 === "string" && callbackUrl_0 !== "") {
        signInOptions_0.callbackUrl = callbackUrl_0;
      }
      signOutAndForget().then(() => signIn("cevi-db", signInOptions_0)).catch(_temp);
    };
    $[4] = currentStepIndex;
    $[5] = formId;
    $[6] = getValues;
    $[7] = signOutAndForget;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleChangeUser = t2;
  let t3;
  if ($[9] !== fieldMapping) {
    t3 = fieldMapping ? JSON.stringify(fieldMapping) : undefined;
    $[9] = fieldMapping;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const fieldMappingString = t3;
  let t4;
  let t5;
  if ($[11] !== currentStepIndex || $[12] !== fieldMappingString || $[13] !== formId || $[14] !== getValues || $[15] !== name || $[16] !== saveField || $[17] !== session || $[18] !== setValue) {
    t4 = () => {
      if (session?.user) {
        let valueToSave;
        bb59: switch (saveField) {
          case "uuid":
            {
              valueToSave = session.user.cevi_db_uuid ?? session.user.uuid;
              break bb59;
            }
          case "name":
            {
              valueToSave = session.user.name;
              break bb59;
            }
          case "nickname":
            {
              valueToSave = session.user.nickname;
              break bb59;
            }
          default:
            {
              valueToSave = session.user.email;
            }
        }
        setValue(name, valueToSave ?? "", {
          shouldValidate: true
        });
        const parsedFieldMapping = fieldMappingString ? JSON.parse(fieldMappingString) as {
          jwtField: string;
          formField: string;
        }[] : undefined;
        if (parsedFieldMapping && parsedFieldMapping.length > 0) {
          let didPrefill = false;
          for (const {
            jwtField,
            formField
          } of parsedFieldMapping) {
            let jwtValue;
            bb86: switch (jwtField) {
              case "name":
                {
                  jwtValue = session.user.name;
                  break bb86;
                }
              case "firstName":
                {
                  jwtValue = session.user.firstName;
                  break bb86;
                }
              case "lastName":
                {
                  jwtValue = session.user.lastName;
                  break bb86;
                }
              case "email":
                {
                  jwtValue = session.user.email;
                  break bb86;
                }
              case "nickname":
                {
                  jwtValue = session.user.nickname;
                  break bb86;
                }
              case "uuid":
                {
                  jwtValue = session.user.uuid;
                  break bb86;
                }
              case "cevi_db_uuid":
                {
                  jwtValue = session.user.cevi_db_uuid;
                  break bb86;
                }
              default:
                {}
            }
            const jwtValueString = jwtValue !== undefined && jwtValue !== null ? String(jwtValue).trim() : "";
            if (jwtValueString.length > 0) {
              const currentValue = getValues(formField) as unknown;
              let currentValueString = "";
              if (typeof currentValue === "string" || typeof currentValue === "number") {
                currentValueString = String(currentValue).trim();
              } else {
                if (currentValue !== undefined && currentValue !== null) {
                  currentValueString = "has_value";
                }
              }
              if (currentValueString.length === 0) {
                setValue(formField, String(jwtValue), {
                  shouldValidate: true
                });
                didPrefill = true;
              }
            }
          }
          if (didPrefill && typeof formId === "string" && currentStepIndex !== undefined) {
            sessionStorage.setItem(getFormStorageKey(formId, "prefill"), String(currentStepIndex + 1));
          }
        }
      }
    };
    t5 = [session, saveField, setValue, name, fieldMappingString, getValues, formId, currentStepIndex];
    $[11] = currentStepIndex;
    $[12] = fieldMappingString;
    $[13] = formId;
    $[14] = getValues;
    $[15] = name;
    $[16] = saveField;
    $[17] = session;
    $[18] = setValue;
    $[19] = t4;
    $[20] = t5;
  } else {
    t4 = $[19];
    t5 = $[20];
  }
  useEffect(t4, t5);
  const errorMessage = error ? (error as FieldError).message : undefined;
  const nickname = session?.user.nickname;
  const hasNickname = typeof nickname === "string" && nickname.length > 0;
  let t6;
  if ($[21] !== label) {
    t6 = Boolean(label) && <SubheadingH3 className="mt-0 mb-2">{label}</SubheadingH3>;
    $[21] = label;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const t7 = errorMessage === undefined ? "border-transparent" : "border-red-500";
  let t8;
  if ($[23] !== t7) {
    t8 = cn("flex flex-col gap-4 rounded-lg border-2 bg-gray-50 p-4", t7);
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== handleChangeUser || $[26] !== handleLogin || $[27] !== hasNickname || $[28] !== locale || $[29] !== nickname || $[30] !== session) {
    t9 = session?.user ? <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm text-gray-600">{loggedInAsText[locale]}:</p><p className="font-semibold text-gray-900">{session.user.name ?? session.user.email}{hasNickname && ` (${nickname})`}</p><p className="text-xs text-gray-500">{session.user.email}</p></div><button type="button" onClick={handleChangeUser} className="text-conveniat-green shrink-0 cursor-pointer text-sm font-medium hover:underline">{changeUserText[locale]}</button></div> : <div className="flex justify-center"><button type="button" onClick={handleLogin} className={cn("font-body relative flex cursor-pointer items-center justify-center rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:outline-none")}><span className="font-body text-center text-sm font-medium text-gray-500">{loginWithCeviDatabaseText[locale]}</span></button></div>;
    $[25] = handleChangeUser;
    $[26] = handleLogin;
    $[27] = hasNickname;
    $[28] = locale;
    $[29] = nickname;
    $[30] = session;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t8 || $[33] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = required === true ? loginRequiredMessage[locale] : false;
  let t12;
  if ($[35] !== name || $[36] !== register || $[37] !== t11) {
    t12 = register(name, {
      required: t11
    });
    $[35] = name;
    $[36] = register;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t12) {
    t13 = <input type="hidden" {...t12} />;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== errorMessage) {
    t14 = Boolean(errorMessage) && <div className="mt-1 text-sm text-red-600">{errorMessage}</div>;
    $[41] = errorMessage;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t10 || $[44] !== t13 || $[45] !== t14 || $[46] !== t6) {
    t15 = <div className="mb-6">{t6}{t10}{t13}{t14}</div>;
    $[43] = t10;
    $[44] = t13;
    $[45] = t14;
    $[46] = t6;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  return t15;
};
function _temp(switchError) {
  console.warn("Switching the Cevi.DB login failed", switchError);
}